/*
 * BLOQUE 1 — Modal de Inscripción a Clases Fijas (autopista única).
 *
 * Sustituye al "Modal Matrícula" embebido (POST directo a /api/cobros, fuente
 * del bug huérfano). Llama al endpoint atómico nuevo:
 *
 *   POST /api/admin/clients/{cliente_id}/inscripcion-clases-fijas
 *
 * Diseñado para reutilizarse en bloques futuros con prop modo:
 *   - "alta"          (Bloque 1, único soportado actualmente)
 *   - "modificacion"  (Bloque 1.2 futuro: machacar líneas de cobro)
 *
 * PR-FIX-INSCRIPCION-CF-SEDES (Ann 2026-05-28) · El modal es la SSoT de la
 * SEDE DE INSCRIPCIÓN: carga horarios + aforo según la sede seleccionada en el
 * propio formulario (form.sede), NO según la sede administrativa previa del
 * cliente. Antes recibía horariosDisponibles/isSlotFull/getSlotInfo/
 * getHorarioIntensidad como props del padre (cargados con sede=client.sede), lo
 * que dejaba los desplegables de hora vacíos o con aforo de otra sede al
 * inscribir en una sede distinta (p.ej. cliente CONSULTAS → CF en MEFIR).
 */

import React from "react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../api";
import CustomColorSelect from "../admin/CustomColorSelect";
// Sub-fase 6.C · selector de turnos compartido + helpers puros + mensajes
import SelectorTurnosFijos from "./SelectorTurnosFijos";
import {
  calcularNumeroTurnosPorCuota,
  construirPayloadTurnoFijo,
  REGLA_DIAS,
} from "../../utils/turnosFijos";
import { mensajeUiPorCodigo } from "../../utils/mensajesInscripcionClasesFijas";

// Sub-fase 6.C · DIAS eliminado · vive dentro de SelectorTurnosFijos.
// PR-FIX-INSCRIPCION-CF-SEDES (Ann 2026-05-27) · CONSULTAS excluido del
// dropdown · no existe CF en sede CONSULTAS (es sede solo clínica/fisio).
// Las clases fijas viven en MUNTANER y MEFIR. Un cliente puede tener CF
// MEFIR + fisio CONSULTAS simultáneamente (cliente mixto · circuitos
// compatibles · regla cerrada en PR-FASE-B-1/B-2).
export const SEDES = ["MUNTANER", "MEFIR"];
// Sedes válidas para detectar default seguro (no usadas como opciones)
const SEDES_CF_VALIDAS = new Set(SEDES);
// Días laborables con clases fijas (idéntico al criterio del padre que
// construye horariosDisponibles · CF no opera fin de semana).
const DIAS_HORARIOS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes"];
const SEDE_CF_DEFAULT = "MUNTANER";

/**
 * Devuelve la sede CF por defecto segura para el modal de inscripción.
 *  - Si el cliente ya tiene sede admin MUNTANER o MEFIR, usar esa.
 *  - Si el cliente viene de CONSULTAS (puro fisio), null o cualquier otro
 *    valor inválido, default seguro "MUNTANER" (CF no existe en CONSULTAS).
 */
export function _sedeCFInicialSegura(clientSede) {
  if (SEDES_CF_VALIDAS.has(clientSede)) return clientSede;
  return SEDE_CF_DEFAULT;
}
const CUOTA_INSCRIPCION_ESTANDAR = 150;

// Sub-fase 6.C · ERROR_MESSAGES eliminado · sustituido por mensajeUiPorCodigo
// (frontend/src/utils/mensajesInscripcionClasesFijas.js) que cubre los códigos
// nuevos de Sub-fase 6.A (CUOTA_HORAS_SEMANA_NO_CONFIGURADA, TURNO_NO_COINCIDE_CUOTA,
// TURNO_INCOMPLETO, TURNO_DUPLICADO, TURNO_DIA_REPETIDO) más los preexistentes.

// 🔴 ANN, 29-09-2026 · DOS PREGUNTAS, NO UNA
//    «primer pago (TPV, efectivo, transferencia, banco) / siguientes cuotas
//     (TPV, efectivo, banco); así evitaremos estos errores».
//    Con una sola casilla, el alta guardaba cómo se pagó la MATRÍCULA (tarjeta o
//    efectivo en recepción) y eso quedaba como forma de pago de TODOS los meses.
//    Medido: fallaba en el 37 % de las altas nuevas; 9 clientas se cayeron de la
//    remesa de octubre — 920,00 € que el banco no iba a cobrar, sin ningún aviso.
const FORMAS_PAGO_OPCIONES = [
  { value: "efectivo", label: "Efectivo" },
  { value: "tarjeta", label: "Tarjeta" },
  { value: "transferencia", label: "Transferencia" },
  { value: "domiciliacion", label: "Domiciliación SEPA" },
  { value: "becado", label: "Becado (sin cobro)" },
];

// El PRIMER pago (matrícula) admite transferencia: es un pago puntual.
const FORMAS_PRIMER_PAGO = [
  { value: "tarjeta", label: "TPV (tarjeta)" },
  { value: "efectivo", label: "Efectivo" },
  { value: "transferencia", label: "Transferencia" },
  { value: "domiciliacion", label: "Banco (domiciliación)" },
];

const ModalInscripcionClasesFijas = ({
  open,
  onClose,
  onSuccess,
  clientId,
  clientNombre,
  clientSede,
  clientFormaPago,
  clientSepa,
  clientCuotaId,
  clientTurnos,
  cuotas,
  modo = "alta",
}) => {
  const hoyISO = new Date().toISOString().split("T")[0];

  const [form, setForm] = React.useState(null);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  // Sub-fase 6.C · estado del SelectorTurnosFijos (validación en tiempo real).
  // ⚠️ Declarado ANTES del `if (!open || !form) return null;` para mantener el
  // orden de hooks constante entre renders (React Rules of Hooks).
  const [estadoTurnos, setEstadoTurnos] = React.useState({ ok: true });

  // Sub-fase 6.C ajuste Ann · callback que recibe filas con contenido que se
  // perderían al recortar (al bajar horas_semana). Mostramos toast.warning
  // explícito para evitar pérdida silenciosa de información del editor.
  // ⚠️ Declarado ANTES del return condicional por la misma razón.
  const handleTurnosRecortados = React.useCallback((filasPerdidas) => {
    if (!Array.isArray(filasPerdidas) || filasPerdidas.length === 0) return;
    const muestra = filasPerdidas
      .map((f) => `${f.dia} ${f.hora}`)
      .join(", ");
    toast.warning(
      `La nueva membresía requiere menos horarios. Se ${
        filasPerdidas.length === 1 ? "ha eliminado el turno sobrante"
                                   : "han eliminado los turnos sobrantes"
      }: ${muestra}.`,
      { duration: 8000 },
    );
  }, []);

  // Inicializar / re-inicializar al abrir
  React.useEffect(() => {
    if (!open) return;
    const turnosIniciales =
      Array.isArray(clientTurnos) && clientTurnos.some((t) => t && t.dia && t.hora)
        ? clientTurnos.filter((t) => t && t.dia && t.hora).map((t) => ({ dia: t.dia, hora: t.hora }))
        : [{ dia: "", hora: "" }];
    setForm({
      fecha_inicio: hoyISO,
      // PR-FIX-INSCRIPCION-CF-SEDES (Ann 2026-05-27) · default seguro · jamás
      // preselecciona CONSULTAS porque CF no existe ahí. Si el cliente viene
      // de CONSULTAS (paciente fisio puro) cae a MUNTANER por defecto y el
      // editor elige MEFIR si procede.
      sede: _sedeCFInicialSegura(clientSede),
      cuota_id: clientCuotaId || "",
      turno_fijo: turnosIniciales,
      forma_pago: clientFormaPago && clientFormaPago !== "becado" ? clientFormaPago : "efectivo",
      // Cómo paga HOY la matrícula. Por defecto, igual que las cuotas.
      forma_pago_primer_pago: clientFormaPago && clientFormaPago !== "becado" ? clientFormaPago : "efectivo",
      ci_importe: CUOTA_INSCRIPCION_ESTANDAR,
      ci_exenta: false,
      ci_motivo: "",
      aplicar_prorrateo: false,
    });
    setShowConfirm(false);
    setSubmitting(false);
  }, [open, clientId, clientSede, clientCuotaId, clientFormaPago, clientTurnos, hoyISO]);

  // ── PR-FIX-INSCRIPCION-CF-SEDES (Ann 2026-05-28) ─────────────────────────
  // Horarios + aforo por SEDE SELECCIONADA EN EL MODAL (form.sede). El padre
  // los cargaba con sede=client.sede; al elegir otra sede aquí los desplegables
  // salían vacíos o con aforo de la sede equivocada. Este efecto recarga ambos
  // cuando cambia la sede del formulario, replicando la lógica del padre
  // (mismo endpoint, mismo mapeo) pero atado a la sede de la inscripción.
  // ⚠️ Hooks declarados ANTES del return condicional (React Rules of Hooks).
  const [horariosDisponiblesSede, setHorariosDisponiblesSede] = React.useState({});
  const [horariosConIntensidadSede, setHorariosConIntensidadSede] = React.useState([]);
  const [aforoDataSede, setAforoDataSede] = React.useState([]);

  const sedeEfectiva = form?.sede || null;

  React.useEffect(() => {
    if (!open || !sedeEfectiva) return;
    let cancelado = false;

    const cargarHorarios = async () => {
      try {
        const resp = await axios.get(`${API}/admin/horarios-fijos/list`, {
          params: { sede: sedeEfectiva },
        });
        const horariosMap = {};
        DIAS_HORARIOS.forEach((d) => (horariosMap[d] = []));
        const conInfo = [];
        (resp.data || []).forEach((h) => {
          if (h.estado === "activo" && DIAS_HORARIOS.includes(h.dia)) {
            if (!horariosMap[h.dia].includes(h.hora)) horariosMap[h.dia].push(h.hora);
            conInfo.push({
              dia: h.dia,
              hora: h.hora,
              intensidad_nombre: h.intensidad_nombre || "Estándar",
              intensidad_color: h.intensidad_color || "#9ca3af",
              capacidad: h.capacidad,
              ocupacion: h.ocupacion,
            });
          }
        });
        DIAS_HORARIOS.forEach((d) =>
          horariosMap[d].sort((a, b) => {
            const [ha, ma] = a.split(":").map(Number);
            const [hb, mb] = b.split(":").map(Number);
            return ha * 60 + ma - (hb * 60 + mb);
          }),
        );
        if (!cancelado) {
          setHorariosDisponiblesSede(horariosMap);
          setHorariosConIntensidadSede(conInfo);
        }
      } catch (e) {
        console.error("Error cargando horarios de la sede del modal", e);
        if (!cancelado) {
          setHorariosDisponiblesSede({});
          setHorariosConIntensidadSede([]);
        }
      }
    };

    const cargarAforo = async () => {
      try {
        const resp = await axios.get(`${API}/admin/aforo-horarios`, {
          params: { sede: sedeEfectiva },
        });
        if (!cancelado) setAforoDataSede(resp.data || []);
      } catch (e) {
        console.error("Error cargando aforo de la sede del modal", e);
        if (!cancelado) setAforoDataSede([]);
      }
    };

    cargarHorarios();
    cargarAforo();
    return () => {
      cancelado = true;
    };
  }, [open, sedeEfectiva]);

  const isSlotFull = React.useCallback(
    (dia, hora) => {
      const slot = aforoDataSede.find((s) => s.dia === dia && s.hora === hora);
      return slot?.lleno || false;
    },
    [aforoDataSede],
  );

  const getSlotInfo = React.useCallback(
    (dia, hora) => {
      const slot = aforoDataSede.find((s) => s.dia === dia && s.hora === hora);
      if (!slot) return null;
      const sedeAforo = sedeEfectiva?.toLowerCase() === "verges" ? 5 : 8;
      return `${slot.ocupados}/${sedeAforo} - ${slot.disponibles} disponibles`;
    },
    [aforoDataSede, sedeEfectiva],
  );

  const getHorarioIntensidad = React.useCallback(
    (dia, hora) => {
      const hInfo = horariosConIntensidadSede.find(
        (h) => h.dia === dia && h.hora === hora,
      );
      return hInfo || { nombre: "Estándar", color: "#9ca3af" };
    },
    [horariosConIntensidadSede],
  );

  if (!open || !form) return null;

  const cuotaSeleccionada = cuotas?.find((c) => String(c.id) === String(form.cuota_id));
  // Sub-fase 6.C · usa helper puro · null si la cuota no tiene horas_semana
  // configuradas (sin fallback peligroso a 2). Esto se alinea con el flag
  // `valida_para_inscripcion` de Sub-fase 6.A.
  const horasMax = calcularNumeroTurnosPorCuota(cuotaSeleccionada);
  const cuotaInvalidaParaInscripcion = (
    !!cuotaSeleccionada
    && cuotaSeleccionada.valida_para_inscripcion === false
  );
  // PR-BECA (Ann 2026-07-06): un becado = alta sin cobro. No se piden datos
  // SEPA; la cuota de inscripción va a 0€ exenta; y los turnos admiten
  // 1..horasMax (no se fuerza el número exacto de la cuota).
  const esBecado = form.forma_pago === "becado";

  // Preview importe mes alta · regla Ann 2026-05-26 (sustituye 2026-05-05):
  // por defecto cuota completa; si admin marca "Prorratea", aplica TABLA
  // SEMANAL FIJA (día 1-7 sem1 / 8-14 sem2 / 15-21 sem3 / 22-fin sem4).
  // Solo cuotas vendibles 87/124/140. Si la cuota no figura en la tabla,
  // el preview avisa y deshabilita la opción para evitar enseñar un importe
  // distinto al que generará el backend.
  // FUENTE DE VERDAD: backend/services/prorrateo_alta_semanal.py
  // (cualquier cambio aquí debe replicarse allí y viceversa).
  const PRORRATEO_ALTA_SEMANA_TABLA = {
    87: { 1: 87, 2: 68, 3: 55, 4: 40 },
    124: { 1: 124, 2: 97, 3: 77, 4: 56 },
    140: { 1: 140, 2: 109, 3: 97, 4: 63 },
  };
  const semanaDelDia = (dia) => {
    if (dia <= 7) return 1;
    if (dia <= 14) return 2;
    if (dia <= 21) return 3;
    return 4;
  };
  const previewMesAlta = (() => {
    // PR-BECA (Ann 2026-07-06): un becado = gratis total · no genera cobros
    // mensuales · el preview refleja 0€ para no prometer un cargo inexistente.
    if (esBecado) {
      return { texto: "0,00 € · Becado (gratis · sin cobros mensuales)", importe: 0 };
    }
    const precio = Number(cuotaSeleccionada?.precio || 0);
    if (!precio || !form.fecha_inicio) return null;
    const [y, m, d] = form.fecha_inicio.split("-").map((n) => parseInt(n, 10));
    if (!y || !m || !d) return null;
    const completo = precio.toFixed(2);
    if (!form.aplicar_prorrateo || d <= 1) {
      return { texto: `${completo} € (cuota completa)`, importe: precio };
    }
    // Flag prorrateo activo y día > 1 → tabla semanal Ann
    const tabla = PRORRATEO_ALTA_SEMANA_TABLA[Math.round(precio)];
    if (!tabla) {
      return {
        texto: `⚠ Cuota ${precio} € no admite prorrateo (cuotas vendibles: 87 / 124 / 140 €). Desmarca "Prorratea" o cambia de cuota.`,
        importe: null,
        bloqueado: true,
      };
    }
    const sem = semanaDelDia(d);
    const importe = tabla[sem];
    return {
      texto: `${importe.toFixed(2)} € (prorrateo semana ${sem} · día ${d} del mes)`,
      importe,
    };
  })();

  // SEPA: faltantes
  // Autopista única Ann 2026-05-26 · misma lógica de 3 ramas que el backend
  // (services/cambiar_forma_cobro_clases_fijas._validar_datos_bancarios · regla
  // legal 29/04/2026: deudor SEPA por defecto = cliente).
  //   Rama 1 · pagador alternativo marcado → exigir pagador_*.
  //   Rama 2 · legacy sepa_titular_cuenta + sepa_titular_dni poblados → OK.
  //   Rama 3 · derivar del cliente (nombre, primer_apellido, dni). DNI legacy
  //            cubre falta cliente.dni.
  // Antes este check exigía sepa_titular_cuenta / sepa_titular_dni LITERAL,
  // sin mirar pagador alternativo ni datos cliente · botón "muerto" cuando el
  // titular era el propio cliente (caso Montserrat Vendrell Rius).
  const sepaFalta = (() => {
    if (form.forma_pago !== "domiciliacion") return [];
    const f = [];
    if (!clientSepa?.iban || String(clientSepa.iban).startsWith("gAAAAA")) {
      f.push("IBAN");
    }
    // Rama 1 · pagador alternativo
    if (clientSepa?.tiene_pagador_alternativo === true) {
      if (!String(clientSepa?.pagador_nombre || "").trim()) f.push("Nombre del pagador");
      if (!String(clientSepa?.pagador_apellidos || "").trim()) f.push("Apellidos del pagador");
      if (!String(clientSepa?.pagador_dni || "").trim()) f.push("DNI del pagador");
      if (!String(clientSepa?.pagador_relacion || "").trim()) f.push("Relación del pagador");
      return f;
    }
    // Rama 2 · legacy sepa_titular_* poblados → respetar (compatibilidad retro)
    const titularLegacy = String(clientSepa?.titular_cuenta || "").trim();
    const dniLegacy = String(clientSepa?.titular_dni || "").trim();
    if (titularLegacy && dniLegacy) {
      return f;
    }
    // Rama 3 · derivar del cliente · titular = cliente, dni titular = cliente.dni
    const cliNombre = String(clientSepa?.cliente_nombre || "").trim();
    const cliPrimerApellido = String(clientSepa?.cliente_primer_apellido || "").trim();
    const cliDni = String(clientSepa?.cliente_dni || "").trim();
    if (!cliNombre) f.push("Nombre del cliente");
    if (!cliPrimerApellido) f.push("Primer apellido del cliente");
    // DNI legacy puede cubrir la falta de cliente.dni (compatibilidad CFC R4f)
    if (!cliDni && !dniLegacy) f.push("DNI del cliente");
    return f;
  })();

  // Sub-fase 6.C · `updateTurno`, `addTurno`, `removeTurno` ELIMINADOS.
  // La gestión de filas ahora vive dentro de <SelectorTurnosFijos>: N filas
  // exactas por horas_semana, sin botones de añadir/quitar manuales.
  // El componente notifica cambios al padre vía la prop `onChange`.
  // `handleTurnosRecortados` está arriba (antes del return condicional) por
  // las Rules of Hooks.

  // Validaciones frontend antes de mostrar confirmación
  const validar = () => {
    const errores = [];
    if (!form.fecha_inicio) errores.push("Fecha de inicio obligatoria");
    if (!form.cuota_id) errores.push("Selecciona una membresía");
    if (!form.sede) errores.push("Selecciona sede");
    if (!form.forma_pago) errores.push("Selecciona forma de pago");

    // Sub-fase 6.C · validación turnos via helper compartido.
    // Bloqueo previo si la cuota no es válida para inscripción.
    // PR-BECA · en modo becado se admite 1..horasMax turnos (no exacto).
    if (cuotaInvalidaParaInscripcion) {
      errores.push(mensajeUiPorCodigo("CUOTA_HORAS_SEMANA_NO_CONFIGURADA"));
    } else if (form.cuota_id && horasMax) {
      try {
        construirPayloadTurnoFijo(form.turno_fijo, {
          horasSemana: horasMax,
          reglaDiasDistintos: REGLA_DIAS.WARNING,
          becado: esBecado,
        });
      } catch (e) {
        errores.push(mensajeUiPorCodigo(e.code, e.detail));
      }
    } else if (!form.cuota_id) {
      errores.push(mensajeUiPorCodigo("CUOTA_ID_REQUERIDO"));
    }

    const importe = Number(form.ci_importe);
    if (Number.isNaN(importe) || importe < 0) errores.push("Importe de cuota de inscripción inválido");
    if (importe > CUOTA_INSCRIPCION_ESTANDAR) errores.push(`Importe no puede superar ${CUOTA_INSCRIPCION_ESTANDAR}€`);
    if (importe === 0 && !form.ci_exenta) errores.push("Marca 'exenta' si el importe es 0");
    if (form.ci_exenta && importe !== 0) errores.push("Una inscripción exenta ha de ir a 0 €");
    if (importe === 0 && !String(form.ci_motivo || "").trim()) errores.push("Motivo de exención obligatorio");
    if (importe > 0 && importe < CUOTA_INSCRIPCION_ESTANDAR && !String(form.ci_motivo || "").trim()) {
      errores.push("Motivo de descuento obligatorio (importe < 150€)");
    }

    if (form.forma_pago === "domiciliacion" && sepaFalta.length > 0) {
      errores.push(`Faltan datos SEPA en ficha: ${sepaFalta.join(", ")}`);
    }

    // Regla Ann 2026-05-26: si admin marca "Prorratea" con una cuota fuera
    // de la tabla semanal (87/124/140), bloquear el envío. El backend
    // habría devuelto 422 CUOTA_SIN_TABLA_PRORRATEO igualmente; mejor
    // cortar aquí y dar mensaje accionable.
    if (previewMesAlta?.bloqueado) {
      errores.push(
        'Esta cuota no admite prorrateo de alta (solo 87 / 124 / 140 €). ' +
        'Desmarca "Prorratea" o selecciona una cuota vendible.'
      );
    }

    return errores;
  };

  const handleRevisar = () => {
    const errores = validar();
    if (errores.length > 0) {
      errores.forEach((e) => toast.error(e));
      return;
    }
    setShowConfirm(true);
  };

  const construirPayload = () => ({
    fecha_inicio: form.fecha_inicio,
    sede: form.sede,
    cuota_id: form.cuota_id,
    // Sub-fase 6.C · helper compartido garantiza turno_fijo válido (completo,
    // sin duplicados, longitud == horas_semana). Si llegara a fallar aquí
    // (no debería, validar() ya lo bloqueó) lanza Error tipado y el catch
    // de handleConfirmar lo recoge.
    turno_fijo: construirPayloadTurnoFijo(form.turno_fijo, {
      horasSemana: horasMax,
      reglaDiasDistintos: REGLA_DIAS.WARNING,
      // PR-BECA · becado envía 1..horasMax turnos completos (sin forzar longitud).
      becado: esBecado,
    }),
    forma_pago: form.forma_pago,                          // ← las CUOTAS de cada mes
    forma_pago_primer_pago: esBecado ? form.forma_pago : form.forma_pago_primer_pago,  // ← la MATRÍCULA
    cuota_inscripcion: {
      importe: Number(form.ci_importe),
      exenta: !!form.ci_exenta,
      motivo: String(form.ci_motivo || "").trim(),
    },
    aplicar_prorrateo: !!form.aplicar_prorrateo,
  });

  const handleConfirmar = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = construirPayload();
      const resp = await axios.post(
        `${API}/admin/clients/${clientId}/inscripcion-clases-fijas`,
        payload
      );
      const data = resp.data || {};
      const cobrosNum = data.resumen?.cobros_mensuales_creados ?? 0;
      toast.success(`Inscripción registrada · ${cobrosNum} cobros mensuales generados`);
      setShowConfirm(false);
      if (typeof onSuccess === "function") {
        await onSuccess(data);
      }
    } catch (err) {
      // Sub-fase 6.C · usa mensajeUiPorCodigo (cubre 6.A + helpers 6.B).
      // Backend devuelve detail = {code, detail: {...}} o detail = string.
      const detailRoot = err?.response?.data?.detail;
      const code = detailRoot?.code || detailRoot?.detail?.code || null;
      const detailObj = detailRoot?.detail
                       || (typeof detailRoot === "object" ? detailRoot : null);
      let msg;
      if (code) {
        msg = mensajeUiPorCodigo(code, detailObj);
      } else if (typeof detailRoot === "string") {
        msg = detailRoot;
      } else if (detailRoot?.detail && typeof detailRoot.detail === "string") {
        msg = detailRoot.detail;
      } else {
        msg = err.message || "Error inesperado al inscribir";
      }
      toast.error(msg, { duration: 8000 });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Estilos minimal inline (mantiene estética cn-* del proyecto) ──
  const overlayStyle = {
    position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  };
  const modalStyle = {
    backgroundColor: "#fff", padding: "20px", borderRadius: "10px",
    width: "min(680px, 92vw)", maxHeight: "92vh", overflowY: "auto",
    boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
  };
  const labelStyle = { fontSize: "12px", color: "#78716c", fontWeight: 600, display: "block", marginBottom: "4px" };
  const inputStyle = { width: "100%", padding: "6px 10px", fontSize: "13px", border: "1px solid #d6d3d1", borderRadius: "6px" };
  const sectionStyle = { marginBottom: "16px" };
  const cuotaNombre = cuotaSeleccionada?.nombre || "—";
  const horarioResumen = (form.turno_fijo || [])
    .filter((t) => t.dia && t.hora)
    .map((t) => `${t.dia} ${t.hora}`)
    .join(", ") || "—";
  const formaPagoLabel = FORMAS_PAGO_OPCIONES.find((o) => o.value === form.forma_pago)?.label || form.forma_pago;
  const ciResumen = form.ci_exenta
    ? `0,00 € · Exenta (${form.ci_motivo || "sin motivo"})`
    : `${Number(form.ci_importe).toFixed(2)} €${form.ci_motivo ? ` · ${form.ci_motivo}` : ""}`;

  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
      <div style={modalStyle}>
        {!showConfirm && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h2 style={{ margin: 0, fontSize: "18px", color: "#16a34a" }}>
                {modo === "alta" ? "Inscribir a clases fijas" : "Modificar inscripción"}
              </h2>
              <button onClick={onClose} disabled={submitting}
                style={{ background: "none", border: "none", fontSize: "20px", cursor: submitting ? "not-allowed" : "pointer", color: "#78716c" }}>
                ×
              </button>
            </div>

            <div style={{ marginBottom: "12px", padding: "8px 10px", backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "6px", fontSize: "12px" }}>
              <strong>Cliente:</strong> {clientNombre || clientId}
            </div>

            {/* Paso 1: Fecha de inicio */}
            <div style={sectionStyle}>
              <label style={labelStyle}>Fecha de inicio de clases</label>
              <input type="date" value={form.fecha_inicio} style={inputStyle}
                onChange={(e) => setForm((p) => ({ ...p, fecha_inicio: e.target.value }))} />
              <div style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                <input type="checkbox" id="aplicar-prorrateo" checked={!!form.aplicar_prorrateo}
                  onChange={(e) => setForm((p) => ({ ...p, aplicar_prorrateo: e.target.checked }))} />
                <label htmlFor="aplicar-prorrateo" style={{ fontSize: "12px", color: "#374151" }}>
                  Prorratea (cobrar solo los días desde la fecha de inicio en el mes de alta)
                </label>
              </div>
              {previewMesAlta && (
                <div style={{ marginTop: "6px", fontSize: "12px", color: "#0f766e", backgroundColor: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: "6px", padding: "6px 8px" }}>
                  Mes de alta: <strong>{previewMesAlta.texto}</strong>
                  {!form.aplicar_prorrateo && <span style={{ color: "#78716c" }}> · marca "Prorratea" si quieres cobrar solo los días</span>}
                </div>
              )}
            </div>

            {/* Paso 2: Cuota de inscripción */}
            <div style={sectionStyle}>
              <label style={labelStyle}>Cuota de inscripción (€)</label>
              <input type="number" step="0.01" min="0" max={CUOTA_INSCRIPCION_ESTANDAR}
                value={form.ci_importe} style={{ ...inputStyle, width: "140px" }}
                onChange={(e) => {
                  const v = e.target.value === "" ? 0 : parseFloat(e.target.value);
                  // PR-CI-EXENTO-2 (Ann 2026-09-07): exenta ⇔ importe 0; al escribir > 0 la marca se QUITA (antes quedaba pegada → «EXENTA · 150 €»)
                  setForm((p) => ({ ...p, ci_importe: v, ci_exenta: v === 0 }));
                }} />
              <div style={{ marginTop: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                <input type="checkbox" id="ci-exenta" checked={!!form.ci_exenta}
                  onChange={(e) => setForm((p) => ({ ...p, ci_exenta: e.target.checked, ci_importe: e.target.checked ? 0 : (Number(p.ci_importe) === 0 ? CUOTA_INSCRIPCION_ESTANDAR : p.ci_importe) }))} />
                <label htmlFor="ci-exenta" style={{ fontSize: "12px", color: "#374151" }}>
                  Marcar como exenta
                </label>
              </div>
              {(Number(form.ci_importe) === 0 || Number(form.ci_importe) < CUOTA_INSCRIPCION_ESTANDAR) && (
                <div style={{ marginTop: "8px" }}>
                  <label style={labelStyle}>
                    {Number(form.ci_importe) === 0 ? "Motivo de exención *" : "Motivo de descuento *"}
                  </label>
                  <input type="text" value={form.ci_motivo} style={inputStyle}
                    placeholder={Number(form.ci_importe) === 0 ? "Ej: becado, convenio empresa..." : "Ej: promoción, familiar..."}
                    onChange={(e) => setForm((p) => ({ ...p, ci_motivo: e.target.value }))} />
                </div>
              )}
              {Number(form.ci_importe) > CUOTA_INSCRIPCION_ESTANDAR && (
                <div style={{ marginTop: "6px", color: "#dc2626", fontSize: "12px" }}>
                  No se permite importe superior a {CUOTA_INSCRIPCION_ESTANDAR}€ en este bloque.
                </div>
              )}
            </div>

            {/* Paso 3: Membresía */}
            <div style={sectionStyle}>
              <label style={labelStyle}>Membresía</label>
              <select value={form.cuota_id} style={inputStyle}
                onChange={(e) => setForm((p) => ({ ...p, cuota_id: e.target.value }))}>
                <option value="">-- Selecciona membresía --</option>
                {/* Sub-fase 6.C · filtra cuotas mal configuradas (valida_para_inscripcion=false).
                    Las cuotas con horas_semana=null no aparecen en el desplegable de
                    inscripción · el admin las ve sólo en el catálogo de gestión. */}
                {(cuotas || [])
                  .filter((c) => c.valida_para_inscripcion !== false)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre} · {c.precio_final || c.precio}€{c.horas_semana ? ` · ${c.horas_semana}h/sem` : ""}
                    </option>
                  ))}
              </select>
              {cuotaSeleccionada && (
                <div style={{ fontSize: "11px", color: "#78716c", marginTop: "4px" }}>
                  Máximo turnos semanales según cuota: {horasMax}
                </div>
              )}
            </div>

            {/* Paso 4: Sede */}
            <div style={sectionStyle}>
              <label style={labelStyle}>Sede</label>
              <select value={form.sede} style={inputStyle}
                onChange={(e) => setForm((p) => ({ ...p, sede: e.target.value }))}>
                {SEDES.map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
              {/* PR-FIX-INSCRIPCION-CF-SEDES (Ann 2026-05-27) · sustituido el
                  warning rojo legacy por un mensaje informativo neutro. La
                  regla nueva por circuitos (PR-FASE-B-1) permite cliente mixto:
                  si el cliente está en CONSULTAS por fisioterapia, puede
                  inscribirse en CF MUNTANER o MEFIR sin perder su circuito
                  clínico (sede_clinica='CONSULTAS' se mantiene intacta · solo
                  cambia sede administrativa). El backend NO rechaza el diff de
                  sede · sobrescribe user.sede con la del payload y valida
                  coherencia post-write vía PC05. */}
              {clientSede && form.sede !== clientSede && (
                <div style={{ marginTop: "4px", color: "#475569", fontSize: "11px" }}>
                  La inscripción de clases fijas se hará en <strong>{form.sede}</strong>.
                  {clientSede === "CONSULTAS" ? (
                    <> Si el cliente tiene fisioterapia activa, seguirá perteneciendo a CONSULTAS para ese circuito.</>
                  ) : (
                    <> La sede administrativa del cliente cambiará a {form.sede} tras la inscripción.</>
                  )}
                </div>
              )}
            </div>

            {/* Paso 5: Horario · Sub-fase 6.C · usa SelectorTurnosFijos compartido */}
            <div style={sectionStyle}>
              <SelectorTurnosFijos
                turnos={form.turno_fijo}
                onChange={(t) => setForm((p) => ({ ...p, turno_fijo: t }))}
                horasSemana={horasMax}
                horariosDisponibles={horariosDisponiblesSede}
                isSlotFull={isSlotFull}
                getSlotInfo={getSlotInfo}
                getHorarioIntensidad={getHorarioIntensidad}
                reglaDiasDistintos={REGLA_DIAS.WARNING}
                becado={esBecado}
                disabled={submitting}
                label={esBecado ? "Horario (becado · elige 1 o más)" : "Horario (turnos fijos)"}
                onValidacion={setEstadoTurnos}
                onTurnosRecortados={handleTurnosRecortados}
                CustomColorSelect={CustomColorSelect}
              />
            </div>

            {/* Paso 6: Forma de pago · DOS preguntas (Ann 2026-09-29) */}
            <div style={sectionStyle}>
              {!esBecado && (
                <>
                  <label style={labelStyle}>Forma de pago del ALTA <span style={{ fontWeight: 400, color: "#78716c" }}>(la matrícula de hoy)</span></label>
                  <select value={form.forma_pago_primer_pago} style={inputStyle}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((p) => ({ ...p, forma_pago_primer_pago: v }));
                    }}>
                    {FORMAS_PRIMER_PAGO.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <div style={{ marginTop: "10px" }} />
                </>
              )}
              <label style={labelStyle}>
                {esBecado ? "Forma de pago" : "Forma de pago del RESTO DE CUOTAS"}
                {!esBecado && <span style={{ fontWeight: 400, color: "#78716c" }}> (cada mes)</span>}
              </label>
              <select value={form.forma_pago} style={inputStyle}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((p) => {
                    // PR-BECA · al elegir "becado" la cuota de inscripción pasa
                    // a 0€ exenta con motivo "Becado" (alta sin cobro). Si el
                    // admin cambia a otra forma de pago, restaura el importe
                    // estándar sólo si venía del auto-becado (importe 0/exenta).
                    if (v === "becado") {
                      return {
                        ...p, forma_pago: v,
                        ci_importe: 0, ci_exenta: true,
                        ci_motivo: String(p.ci_motivo || "").trim() || "Becado",
                      };
                    }
                    if (p.forma_pago === "becado"
                        && Number(p.ci_importe) === 0 && p.ci_exenta) {
                      return {
                        ...p, forma_pago: v,
                        ci_importe: CUOTA_INSCRIPCION_ESTANDAR,
                        ci_exenta: false, ci_motivo: "",
                      };
                    }
                    return { ...p, forma_pago: v };
                  });
                }}>
                {FORMAS_PAGO_OPCIONES
                  // La transferencia NO vale para cobrar cada mes (Ann 2026-09-29):
                  // sirve para un pago puntual. El servidor también la rechaza.
                  .filter((o) => o.value !== "transferencia")
                  .map((o) => (
                    <option key={o.value} value={o.value} disabled={o.disabled}>
                      {o.label}
                    </option>
                  ))}
              </select>
              {!esBecado && form.forma_pago !== form.forma_pago_primer_pago && (
                <div style={{ marginTop: "8px", padding: "8px 10px", backgroundColor: "#eff6ff",
                              border: "1px solid #bfdbfe", borderRadius: "6px", fontSize: "12px", color: "#1e3a8a" }}>
                  Hoy paga <strong>{(FORMAS_PRIMER_PAGO.find((o) => o.value === form.forma_pago_primer_pago) || {}).label}</strong>{" "}
                  y a partir del mes que viene se le cobrará por{" "}
                  <strong>{(FORMAS_PAGO_OPCIONES.find((o) => o.value === form.forma_pago) || {}).label}</strong>.
                </div>
              )}
              {form.forma_pago === "domiciliacion" && (
                <div style={{ marginTop: "8px", padding: "8px 10px", backgroundColor: sepaFalta.length > 0 ? "#fef2f2" : "#f0fdf4", border: `1px solid ${sepaFalta.length > 0 ? "#fecaca" : "#bbf7d0"}`, borderRadius: "6px", fontSize: "12px" }}>
                  {sepaFalta.length > 0 ? (
                    <>
                      <strong style={{ color: "#991b1b" }}>Faltan datos SEPA:</strong>{" "}
                      <span style={{ color: "#991b1b" }}>{sepaFalta.join(", ")}</span>
                      <div style={{ marginTop: "4px", color: "#78716c", fontSize: "11px" }}>
                        Completa los datos bancarios en la pestaña Datos personales del cliente y vuelve a intentar.
                      </div>
                    </>
                  ) : (
                    <span style={{ color: "#16a34a" }}>
                      ✓ Se usarán los datos bancarios ya guardados en la ficha del cliente. No se pide IBAN nuevo aquí.
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Footer · Ann 2026-05-26 · motivo visible cuando el botón está
                disabled. Antes el botón parecía "muerto" sin pista (caso
                Montserrat Vendrell Rius · faltaban Titular + DNI titular SEPA). */}
            {(() => {
              const motivos = [];
              if (form.forma_pago === "domiciliacion" && sepaFalta.length > 0) {
                motivos.push(`Faltan datos SEPA en la ficha: ${sepaFalta.join(", ")}`);
              }
              if (Number(form.ci_importe) > CUOTA_INSCRIPCION_ESTANDAR) {
                motivos.push(`Importe cuota inscripción > ${CUOTA_INSCRIPCION_ESTANDAR}€`);
              }
              if (cuotaInvalidaParaInscripcion) {
                motivos.push("La membresía elegida no es válida para inscripción (horas_semana no configuradas)");
              }
              if (estadoTurnos.ok === false) {
                motivos.push("Turnos incompletos o mal configurados (revisa los días/horas)");
              }
              if (motivos.length === 0) return null;
              return (
                <div style={{ marginTop: "12px", padding: "8px 10px", backgroundColor: "#fef3c7", border: "1px solid #fde68a", borderRadius: "6px", fontSize: "12px", color: "#92400e" }}>
                  <strong>⚠ No puedes confirmar todavía:</strong>
                  <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
                    {motivos.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              );
            })()}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "16px", paddingTop: "12px", borderTop: "1px solid #e5e5e5" }}>
              <button onClick={onClose} disabled={submitting}
                style={{ fontSize: "13px", padding: "6px 14px", backgroundColor: "#fff", color: "#78716c", border: "1px solid #d6d3d1", borderRadius: "5px", cursor: submitting ? "not-allowed" : "pointer" }}>
                Cancelar
              </button>
              <button onClick={handleRevisar}
                disabled={
                  submitting
                  || (form.forma_pago === "domiciliacion" && sepaFalta.length > 0)
                  || Number(form.ci_importe) > CUOTA_INSCRIPCION_ESTANDAR
                  /* Sub-fase 6.C · bloqueo si cuota mal configurada o turnos
                     no cumplen reglas (estadoTurnos.ok=false). El componente
                     SelectorTurnosFijos lo notifica en tiempo real. */
                  || cuotaInvalidaParaInscripcion
                  || estadoTurnos.ok === false
                }
                title={
                  submitting ? "Procesando..." :
                  (form.forma_pago === "domiciliacion" && sepaFalta.length > 0) ?
                    `Faltan datos SEPA: ${sepaFalta.join(", ")}` :
                  Number(form.ci_importe) > CUOTA_INSCRIPCION_ESTANDAR ?
                    `Importe cuota inscripción > ${CUOTA_INSCRIPCION_ESTANDAR}€` :
                  cuotaInvalidaParaInscripcion ?
                    "Cuota no válida para inscripción" :
                  estadoTurnos.ok === false ?
                    "Turnos mal configurados" :
                  "Revisar y confirmar inscripción"
                }
                style={{ fontSize: "13px", padding: "6px 14px",
                  backgroundColor: (
                    submitting
                    || (form.forma_pago === "domiciliacion" && sepaFalta.length > 0)
                    || Number(form.ci_importe) > CUOTA_INSCRIPCION_ESTANDAR
                    || cuotaInvalidaParaInscripcion
                    || estadoTurnos.ok === false
                  ) ? "#9ca3af" : "#16a34a",
                  color: "#fff", border: "none", borderRadius: "5px",
                  cursor: (
                    submitting
                    || (form.forma_pago === "domiciliacion" && sepaFalta.length > 0)
                    || Number(form.ci_importe) > CUOTA_INSCRIPCION_ESTANDAR
                    || cuotaInvalidaParaInscripcion
                    || estadoTurnos.ok === false
                  ) ? "not-allowed" : "pointer",
                  opacity: submitting ? 0.6 : 1 }}>
                Revisar y confirmar
              </button>
            </div>
          </>
        )}

        {showConfirm && (
          <>
            <h2 style={{ margin: "0 0 12px 0", fontSize: "18px", color: "#16a34a" }}>Confirmar inscripción</h2>
            <div style={{ padding: "12px 14px", backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "6px", fontSize: "13px", lineHeight: 1.5 }}>
              Vas a inscribir a <strong>{clientNombre || clientId}</strong> en clases fijas.
              <div style={{ marginTop: "10px" }}>
                <div><strong>Fecha de inicio:</strong> {form.fecha_inicio}</div>
                <div><strong>Membresía:</strong> {cuotaNombre}</div>
                <div><strong>Horario:</strong> {horarioResumen}</div>
                <div><strong>Sede:</strong> {form.sede}</div>
                <div><strong>Forma de pago:</strong> {formaPagoLabel}</div>
                <div><strong>Cuota de inscripción:</strong> {ciResumen}</div>
                <div>
                  <strong>Prorratea mes de alta:</strong>{" "}
                  {form.aplicar_prorrateo ? "Sí (prorrateo proporcional)" : "No (cuota completa)"}
                </div>
                {previewMesAlta && (
                  <div><strong>Mes de alta:</strong> {previewMesAlta.texto}</div>
                )}
              </div>
              <div style={{ marginTop: "10px", color: "#78716c" }}>
                {esBecado
                  ? "Al confirmar, se dará de alta al becado en clases fijas GRATIS: no se generarán cobros mensuales ni cuota de inscripción."
                  : "Al confirmar, se generarán las líneas de cobro desde la fecha de inicio hasta final de año."}
              </div>
              <div style={{ marginTop: "8px" }}>¿Quieres continuar?</div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "16px" }}>
              <button onClick={() => setShowConfirm(false)} disabled={submitting}
                style={{ fontSize: "13px", padding: "6px 14px", backgroundColor: "#fff", color: "#78716c", border: "1px solid #d6d3d1", borderRadius: "5px", cursor: submitting ? "not-allowed" : "pointer" }}>
                Volver
              </button>
              <button onClick={handleConfirmar} disabled={submitting}
                style={{ fontSize: "13px", padding: "6px 14px", backgroundColor: submitting ? "#9ca3af" : "#16a34a", color: "#fff", border: "none", borderRadius: "5px", cursor: submitting ? "not-allowed" : "pointer" }}>
                {submitting ? "Inscribiendo..." : "Confirmar inscripción"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ModalInscripcionClasesFijas;
